import { CompassTwoTone, InfoCircleTwoTone } from "@ant-design/icons";
import { Button, notification } from "antd";
import { Component, ErrorInfo, ReactNode } from "react";
import { postDevFrontDebug } from "tm-shared/api";
import { createUniqueId, deepParseJson } from "tm-shared/utils";

import DarkLogo from "@/assets/icons/logo-with-text-dark.svg";
import LightLogo from "@/assets/icons/logo-with-text-light.svg";
import { APP_VERSION } from "@/config";
import { openReplayService } from "@/Services/OpenReplay";
import { getTheme } from "@/store/slices/theme";

import {
    StyledErrorBoundary,
    StyledErrorBoundaryBottom,
    StyledErrorBoundaryDetails,
    StyledErrorBoundaryImage,
    StyledErrorBoundaryInfoBlock,
    StyledErrorBoundaryInfoText,
    StyledErrorBoundaryTitle,
    StyledErrorBoundaryTop,
    StyledErrorControllers,
    StyledErrorHeader,
    StyledErrorVersionText,
} from "./Styled";

interface Props {
    children: ReactNode;
}

interface DebugInfo {
    state?: Record<string, unknown>;
    error?: string;
    errorInfo?: ErrorInfo;
    currentRoute?: string;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
    currentRoute?: string;
    persistState?: Record<string, unknown>;
    isSendingSupportRequest: boolean;
    isSupportButtonDisabled: boolean;
    lastErrorSignature?: string;
}

class ErrorBoundary extends Component<Props, State> {
    state: State = {
        hasError: false,
        isSendingSupportRequest: false,
        isSupportButtonDisabled: false,
    };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error,
            lastErrorSignature: error.message + error.stack,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("ErrorBoundary caught an error", error, errorInfo);

        openReplayService.startOnError("react_error_boundary", {
            message: error.message,
            route: window.location.pathname,
        });

        const newErrorSignature = error.message + error.stack;
        const currentRoute = window.location.pathname;
        const persistState = this.safeParseJSON(
            localStorage.getItem("persist:root"),
        );

        this.setState((prevState) => ({
            error,
            errorInfo,
            currentRoute,
            persistState,
            isSupportButtonDisabled:
                prevState.lastErrorSignature !== newErrorSignature
                    ? false
                    : prevState.isSupportButtonDisabled,
        }));
    }

    safeParseJSON = (
        data: string | null,
    ): Record<string, unknown> | undefined => {
        if (!data) return undefined;
        try {
            return JSON.parse(data) as Record<string, unknown>;
        } catch {
            return undefined;
        }
    };

    handleReload = (): void => window.location.reload();

    handleClearCache = (): void => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = `/login?reset=${createUniqueId()}`;
        } catch (e) {
            console.error("Error clearing cache", e);
        }
    };

    handleReturnProfile = (): void => {
        window.location.href = `/profile?reset=${createUniqueId()}`;
    };

    handleSupportRequest = async (): Promise<void> => {
        this.setState({ isSendingSupportRequest: true });
        const { error, errorInfo, currentRoute, persistState } = this.state;
        const persistStateDeepParsed = deepParseJson(persistState) as Record<
            string,
            unknown
        >;
        try {
            const debugInfo: DebugInfo = {
                state: persistStateDeepParsed,
                error: error?.toString() || "Невідома помилка",
                errorInfo,
                currentRoute,
            };
            await postDevFrontDebug({
                user:
                    (persistStateDeepParsed?.user as { id?: string })?.id ||
                    "No data",
                debugInfo,
            });

            this.setState({ isSupportButtonDisabled: true });

            notification.success({
                message: "Успішно надіслано інформацію тех-підтримці. Дякуємо!",
                placement: "topRight",
            });
        } catch (error) {
            notification.error({
                message: "Помилка при надсиланні інформації тех-підтримці",
                description:
                    error instanceof Error ? error.message : String(error),
                placement: "topRight",
            });
        } finally {
            this.setState({ isSendingSupportRequest: false });
        }
    };

    renderErrorInfoBlock = (icon: ReactNode, message: string): JSX.Element => (
        <StyledErrorBoundaryInfoBlock>
            {icon}
            <StyledErrorBoundaryInfoText theme={getTheme()}>
                {message}
            </StyledErrorBoundaryInfoText>
        </StyledErrorBoundaryInfoBlock>
    );

    render(): ReactNode {
        if (!this.state.hasError) return this.props.children;

        const {
            error,
            errorInfo,
            currentRoute,
            isSendingSupportRequest,
            isSupportButtonDisabled,
        } = this.state;
        const theme = getTheme();

        return (
            <StyledErrorBoundary theme={theme}>
                <StyledErrorHeader>
                    <StyledErrorBoundaryImage
                        src={theme === "dark" ? DarkLogo : LightLogo}
                        alt="Trade Master Logo"
                    />
                    <StyledErrorVersionText theme={theme}>
                        v{APP_VERSION}
                    </StyledErrorVersionText>
                </StyledErrorHeader>

                <StyledErrorBoundaryTop>
                    <StyledErrorBoundaryTitle>
                        Помилка мобільного <br /> застосунку
                    </StyledErrorBoundaryTitle>
                    {this.renderErrorInfoBlock(
                        <CompassTwoTone style={{ fontSize: "28px" }} />,
                        `Поточний маршрут: ${currentRoute || "Невідомо"}`,
                    )}
                    {this.renderErrorInfoBlock(
                        <InfoCircleTwoTone style={{ fontSize: "28px" }} />,
                        error?.toString() || "Невідома помилка",
                    )}

                    {errorInfo && (
                        <StyledErrorBoundaryDetails>
                            <summary>Деталі помилки</summary>
                            <pre>{errorInfo.componentStack}</pre>
                        </StyledErrorBoundaryDetails>
                    )}
                </StyledErrorBoundaryTop>
                <StyledErrorBoundaryBottom theme={getTheme()}>
                    <Button
                        type="default"
                        onClick={this.handleSupportRequest}
                        loading={isSendingSupportRequest}
                        style={{ marginTop: 50, width: 200 }}
                        disabled={isSupportButtonDisabled}
                    >
                        Повідомити підтримку
                    </Button>
                    <StyledErrorControllers>
                        <Button type="primary" onClick={this.handleReload}>
                            Перезавантажити
                        </Button>
                        <Button
                            type="primary"
                            onClick={this.handleReturnProfile}
                        >
                            Повернутися у профіль
                        </Button>
                    </StyledErrorControllers>
                </StyledErrorBoundaryBottom>
            </StyledErrorBoundary>
        );
    }
}

export default ErrorBoundary;
